import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ImageUploader, StringListEditor } from './_helpers';

const FieldText = ({ label, value, onChange, multiline = false, placeholder = '' }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
    {multiline ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" rows={3} placeholder={placeholder} />
    ) : (
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder={placeholder} />
    )}
  </div>
);

const FieldNumber = ({ label, value, onChange, placeholder = '' }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
    <input type="number" value={value || ''} onChange={(e) => onChange(parseInt(e.target.value) || 0)} className="w-full border rounded px-3 py-2 text-sm" placeholder={placeholder} />
  </div>
);

const FieldDate = ({ label, value, onChange, hint = '' }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
    <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const FieldSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const ItemListEditor = ({ items = [], fields, onChange, label = 'Items' }) => {
  const update = (idx, key, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const add = () => onChange([...items, fields.reduce((acc, f) => ({ ...acc, [f.key]: f.default ?? '' }), {})]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{label}</label>
      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={idx} className="border rounded p-3 bg-gray-50">
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs font-semibold text-gray-500">Item {idx + 1}</span>
              <button onClick={() => remove(idx)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {fields.map((f) =>
                f.type === 'list' ? (
                  <div key={f.key} className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{f.label}</label>
                    <StringListEditor items={it[f.key] || []} onChange={(v) => update(idx, f.key, v)} placeholder={f.placeholder || ''} />
                  </div>
                ) : f.type === 'textarea' ? (
                  <div key={f.key} className="md:col-span-2">
                    <FieldText label={f.label} value={it[f.key]} onChange={(v) => update(idx, f.key, v)} multiline placeholder={f.placeholder} />
                  </div>
                ) : f.type === 'image' ? (
                  <div key={f.key} className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{f.label}</label>
                    <ImageUploader currentImage={it[f.key]} onUpload={(url) => update(idx, f.key, url)} />
                    <input type="text" value={it[f.key] || ''} onChange={(e) => update(idx, f.key, e.target.value)} placeholder="or paste URL" className="w-full border rounded px-3 py-2 text-sm mt-2" />
                  </div>
                ) : (
                  <FieldText key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(idx, f.key, v)} placeholder={f.placeholder} />
                )
              )}
            </div>
          </div>
        ))}
      </div>
      <button onClick={add} className="text-[#C9A86C] hover:underline text-sm flex items-center gap-1 mt-2"><Plus size={14} /> Add Item</button>
    </div>
  );
};

const InlineSubBlock = ({ value, onChange }) => (
  <div className="border-l-2 border-gray-300 pl-3 space-y-2">
    <FieldSelect label="Sub-block Type" value={value?.type} onChange={(v) => onChange({ type: v })} options={[
      { value: 'bullet_list', label: 'Bullet List' },
      { value: 'details_grid', label: 'Details Grid (label/value)' },
    ]} />
    {value?.type === 'bullet_list' && (
      <>
        <FieldText label="Title" value={value.title} onChange={(v) => onChange({ ...value, title: v })} />
        <StringListEditor items={value.items || []} onChange={(items) => onChange({ ...value, items })} placeholder="Bullet text" />
      </>
    )}
    {value?.type === 'details_grid' && (
      <>
        <FieldText label="Title" value={value.title} onChange={(v) => onChange({ ...value, title: v })} />
        <ItemListEditor items={value.items || []} fields={[{ key: 'label', label: 'Label' }, { key: 'value', label: 'Value' }]} onChange={(items) => onChange({ ...value, items })} label="Pairs" />
      </>
    )}
  </div>
);

const SectionEditor = ({ section, onChange }) => {
  const data = section.data || {};
  const setData = (patch) => onChange({ ...section, data: { ...data, ...patch } });
  switch (section.type) {
    case 'rich_text':
      return (
        <div className="space-y-3">
          <FieldText label="Title (optional)" value={data.title} onChange={(v) => setData({ title: v })} />
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Paragraphs</label>
            <StringListEditor items={data.paragraphs || []} onChange={(paragraphs) => setData({ paragraphs })} placeholder="Paragraph text" textarea />
          </div>
        </div>
      );
    case 'card_with_image':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Image</label>
            <ImageUploader currentImage={data.image_url} onUpload={(url) => setData({ image_url: url })} />
            <input type="text" value={data.image_url || ''} onChange={(e) => setData({ image_url: e.target.value })} placeholder="or paste URL" className="w-full border rounded px-3 py-2 text-sm mt-2" />
          </div>
          <FieldText label="Name" value={data.name} onChange={(v) => setData({ name: v })} />
          <FieldText label="Title (subtitle under name)" value={data.title} onChange={(v) => setData({ title: v })} />
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Paragraphs</label>
            <StringListEditor items={data.paragraphs || []} onChange={(paragraphs) => setData({ paragraphs })} textarea />
          </div>
          <FieldText label="Footer Label (optional bold prefix)" value={data.footer_label} onChange={(v) => setData({ footer_label: v })} />
          <FieldText label="Footer Body" value={data.footer_body} onChange={(v) => setData({ footer_body: v })} multiline />
        </div>
      );
    case 'feature_grid':
      return (
        <div className="space-y-3">
          <FieldNumber label="Columns (2-4)" value={data.columns} onChange={(v) => setData({ columns: v })} />
          <ItemListEditor items={data.items || []} onChange={(items) => setData({ items })} fields={[
            { key: 'icon', label: 'Lucide Icon Name (e.g. Sparkles, Wine, Users)' },
            { key: 'title', label: 'Title' },
            { key: 'description', label: 'Description', type: 'textarea' },
          ]} label="Cards" />
        </div>
      );
    case 'two_column':
      return (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-600 uppercase">Left Column</p>
          <InlineSubBlock value={data.left} onChange={(v) => setData({ left: v })} />
          <p className="text-xs font-semibold text-gray-600 uppercase mt-3">Right Column</p>
          <InlineSubBlock value={data.right} onChange={(v) => setData({ right: v })} />
        </div>
      );
    case 'bullet_list':
      return (
        <div className="space-y-3">
          <FieldText label="Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <StringListEditor items={data.items || []} onChange={(items) => setData({ items })} placeholder="Bullet text" />
        </div>
      );
    case 'details_grid':
      return (
        <div className="space-y-3">
          <FieldText label="Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <ItemListEditor items={data.items || []} fields={[{ key: 'label', label: 'Label' }, { key: 'value', label: 'Value' }]} onChange={(items) => setData({ items })} label="Pairs" />
        </div>
      );
    case 'cta_button':
      return (
        <div className="space-y-3">
          <FieldText label="Button Text" value={data.text} onChange={(v) => setData({ text: v })} />
          <FieldText label="Link (URL or mailto:)" value={data.link} onChange={(v) => setData({ link: v })} />
          <FieldText label="Note (below button)" value={data.note} onChange={(v) => setData({ note: v })} />
        </div>
      );
    case 'initiatives_grid':
      return (
        <div className="space-y-3">
          <FieldSelect label="Source" value={data.source || 'community'} onChange={(v) => setData({ source: v })} options={[{ value: 'community', label: 'Community Initiatives Collection' }]} />
          <p className="text-xs text-gray-500">Pulls live from the Community manager. Edit individual initiatives there.</p>
        </div>
      );
    case 'mission_card':
      return (
        <div className="space-y-3">
          <FieldText label="Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <FieldText label="Body" value={data.body} onChange={(v) => setData({ body: v })} multiline />
          <FieldText label="Primary Button Text" value={data.primary_text} onChange={(v) => setData({ primary_text: v })} />
          <FieldText label="Primary Link" value={data.primary_link} onChange={(v) => setData({ primary_link: v })} />
          <FieldText label="Secondary Button Text" value={data.secondary_text} onChange={(v) => setData({ secondary_text: v })} />
          <FieldText label="Secondary Link" value={data.secondary_link} onChange={(v) => setData({ secondary_link: v })} />
        </div>
      );
    case 'info_card_grid':
      return (
        <div className="space-y-3">
          <FieldText label="Section Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <FieldNumber label="Columns (2-3)" value={data.columns} onChange={(v) => setData({ columns: v })} />
          <ItemListEditor items={data.items || []} onChange={(items) => setData({ items })} fields={[
            { key: 'name', label: 'Name' },
            { key: 'capacity', label: 'Capacity (optional)' },
            { key: 'description', label: 'Description', type: 'textarea' },
            { key: 'features', label: 'Features', type: 'list' },
          ]} label="Cards" />
        </div>
      );
    case 'info_card_grid_packages':
      return (
        <div className="space-y-3">
          <FieldText label="Section Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <FieldSelect label="Source" value={data.source || 'private_events'} onChange={(v) => setData({ source: v })} options={[{ value: 'private_events', label: 'Private Events Collection' }]} />
          <p className="text-xs text-gray-500">Pulls from Private Events manager.</p>
        </div>
      );
    case 'venue_card':
      return (
        <div className="space-y-3">
          <FieldText label="Name" value={data.name} onChange={(v) => setData({ name: v })} />
          <FieldText label="Capacity" value={data.capacity} onChange={(v) => setData({ capacity: v })} />
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Features</label>
            <StringListEditor items={data.features || []} onChange={(features) => setData({ features })} placeholder="Feature text" />
          </div>
        </div>
      );
    case 'inquiry_form':
      return (
        <div className="space-y-3">
          <FieldText label="Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <FieldText label="Subtitle" value={data.subtitle} onChange={(v) => setData({ subtitle: v })} />
          <FieldText
            label="Pre-Opening Notice (optional, shown above form)"
            value={data.notice}
            onChange={(v) => setData({ notice: v })}
            multiline
            placeholder="e.g. Brick Lane opens June 29 — we're accepting private event inquiries for that date onward."
          />
          <FieldDate
            label="Earliest Allowed Event Date"
            value={data.earliest_event_date}
            onChange={(v) => setData({ earliest_event_date: v })}
            hint="Date picker on the public form will block any selection before this. Leave blank to allow any future date."
          />
          <FieldText label="Success Toast Message" value={data.success_message} onChange={(v) => setData({ success_message: v })} />
          <FieldText label="Footer Note (below button)" value={data.footer_note} onChange={(v) => setData({ footer_note: v })} />
        </div>
      );
    case 'contact_block':
      return (
        <div className="space-y-3">
          <FieldText label="Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <FieldSelect label="Source" value={data.source || 'restaurant_info'} onChange={(v) => setData({ source: v })} options={[{ value: 'restaurant_info', label: 'Restaurant Info Collection' }]} />
          <p className="text-xs text-gray-500">Address/phone/email/hours pulled from Restaurant Info manager.</p>
        </div>
      );
    case 'map_embed':
      return (
        <div className="space-y-3">
          <FieldText label="Iframe Src URL" value={data.src} onChange={(v) => setData({ src: v })} multiline />
          <FieldNumber label="Height (px)" value={data.height} onChange={(v) => setData({ height: v })} />
          <FieldText label="Title (a11y)" value={data.title} onChange={(v) => setData({ title: v })} />
        </div>
      );
    case 'reservation_card':
      return (
        <div className="space-y-3">
          <FieldText label="Section Title" value={data.title} onChange={(v) => setData({ title: v })} />
          <FieldText label="Card Heading" value={data.heading} onChange={(v) => setData({ heading: v })} />
          <FieldText label="Body" value={data.body} onChange={(v) => setData({ body: v })} multiline />
          <FieldText label="Primary Button Text" value={data.primary_text} onChange={(v) => setData({ primary_text: v })} />
          <FieldText label="Primary Link" value={data.primary_link} onChange={(v) => setData({ primary_link: v })} />
          <FieldText label="Phone Label" value={data.phone_label} onChange={(v) => setData({ phone_label: v })} />
          <FieldText label="Tips Title" value={data.tips_title} onChange={(v) => setData({ tips_title: v })} />
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Tips</label>
            <StringListEditor items={data.tips || []} onChange={(tips) => setData({ tips })} placeholder="Tip text" />
          </div>
        </div>
      );
    case 'menu_items_list':
      return (
        <div className="space-y-3">
          <FieldText label="Menu Category Name (must match menu_items.category)" value={data.category} onChange={(v) => setData({ category: v })} placeholder="e.g. Chaat" />
        </div>
      );
    case 'note':
      return <FieldText label="Note Text" value={data.text} onChange={(v) => setData({ text: v })} multiline />;
    case 'html':
      return <FieldText label="Raw HTML" value={data.html} onChange={(v) => setData({ html: v })} multiline />;
    default:
      return <p className="text-sm text-gray-500">Unknown section type: {section.type}</p>;
  }
};

export default SectionEditor;
